import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Popover,
  Select,
  Skeleton,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  Pagination,
} from '@mui/material';
import { addDays, format, isSameDay } from 'date-fns';
import { DateRangePicker } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import PageHeader from './PageHeader';
import WorkstationTimelineModal from './WorkstationTimelineModal';
import { useImagePreview } from './ImagePreviewDialog';
import { formatDate, formatDateOnly } from '../utils/dateUtils';
import {
  createTodayDateRange,
  DEFAULT_END_TIME,
  DEFAULT_START_TIME,
} from '../utils/filterDefaults';
import {
  pageFilterAutocompleteSx,
  pageFilterDatePickerSx,
  pageFilterFormControlSx,
  pageFiltersControlsSx,
  pageFiltersStackSx,
  pageFiltersToolbarSx,
} from '../utils/pageFilterStyles';
import {
  getROIConfig,
  getWorkstationCameras,
  getWorkstationOccupancySummary,
  getWorkstationOccupancyTimeline,
  getWorkstationSnapshotImageBlob,
  getWorkstationSnapshots,
} from '../utils/api';

const primary = '#40518A';
const WORKSTATION_TYPE = 'WORKSTATION';
const ITEMS_PER_VIEW = 3;
/** Occupancy chips stay live. Snapshots are saved on occupied/idle changes. */
const OCCUPANCY_REFRESH_MS = 30000;
const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const todayLocalDate = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const parseDateKey = (key) => {
  const [y, m, d] = String(key || '').split('-').map(Number);
  if (!y || !m || !d) return new Date();
  return new Date(y, (m || 1) - 1, d || 1);
};

const toDateKey = (value) => {
  const d = value instanceof Date ? value : new Date(value);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const formatRangeLabel = (startDate, endDate) => {
  const start = parseDateKey(startDate);
  const end = parseDateKey(endDate);
  if (isSameDay(start, end)) return format(start, 'MMM dd, yyyy');
  return `${format(start, 'MMM dd')} - ${format(end, 'MMM dd, yyyy')}`;
};

const createAppliedQuery = (range, startTime, endTime) => ({
  startDate: toDateKey(range?.[0]?.startDate || new Date()),
  endDate: toDateKey(range?.[0]?.endDate || range?.[0]?.startDate || new Date()),
  startTime: startTime || DEFAULT_START_TIME,
  endTime: endTime || DEFAULT_END_TIME,
});

const formatWorkstationLabel = (workstationId) => {
  const text = String(workstationId ?? '').trim();
  if (!text) return '—';
  const stripped = text.replace(/^(workstation|desk|workspace)[\s_-]*/i, '').trim() || text;
  return `Workstation-${stripped}`;
};

const formatDuration = (seconds) => {
  if (seconds == null || Number.isNaN(Number(seconds))) return '—';
  const total = Math.max(0, Math.floor(Number(seconds)));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
};

const formatRelativeActivity = (iso) => {
  if (!iso) return 'Never';
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return '—';
  const diffSec = Math.max(0, Math.floor((Date.now() - ts) / 1000));
  if (diffSec < 60) return 'Just now';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  return formatDateOnly(iso);
};

const sortWorkstationIds = (ids) =>
  [...ids].sort((a, b) => {
    const na = parseInt(String(a).replace(/\D/g, ''), 10);
    const nb = parseInt(String(b).replace(/\D/g, ''), 10);
    if (Number.isFinite(na) && Number.isFinite(nb) && na !== nb) return na - nb;
    return String(a).localeCompare(String(b));
  });

const ActivityTile = ({ snapshot, label, onOpen }) => (
  <Paper
    variant="outlined"
    sx={{
      borderRadius: 2,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
    }}
  >
    <Box sx={{ position: 'relative', height: { xs: 80, sm: 100, md: 110 }, background: '#0f172a' }}>
      {snapshot?.src ? (
        <Box
          component="img"
          src={snapshot.src}
          alt={label}
          onClick={onOpen}
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            cursor: 'pointer',
          }}
        />
      ) : (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'rgba(255,255,255,0.5)',
            fontSize: '0.7rem',
          }}
        >
          No frame
        </Box>
      )}
    </Box>
    <Box sx={{ p: { xs: 0.75, sm: 1 } }}>
      <Typography
        variant="body2"
        noWrap
        sx={{ fontWeight: 600, fontSize: { xs: '0.7rem', sm: '0.75rem' }, lineHeight: 1.3 }}
      >
        {snapshot?.capturedAt ? formatDate(snapshot.capturedAt, true, false) : '--:--:--'}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        noWrap
        sx={{ fontSize: { xs: '0.6rem', sm: '0.65rem' }, lineHeight: 1.2 }}
      >
        {snapshot?.capturedAt ? formatDateOnly(snapshot.capturedAt) : '—'}
      </Typography>
      {snapshot?.state === 'occupied' || snapshot?.state === 'idle' ? (
        <Typography
          variant="caption"
          noWrap
          sx={{
            display: 'block',
            fontWeight: 700,
            fontSize: { xs: '0.6rem', sm: '0.65rem' },
            lineHeight: 1.2,
            color: snapshot.state === 'occupied' ? '#166534' : '#475569',
          }}
        >
          {snapshot.state === 'occupied' ? 'Occupied' : 'Idle'}
        </Typography>
      ) : null}
    </Box>
  </Paper>
);

const tileFlexSx = {
  flex: `0 0 calc(${100 / ITEMS_PER_VIEW}% - ${((ITEMS_PER_VIEW - 1) * 9.6) / ITEMS_PER_VIEW}px)`,
  minWidth: 90,
};

const ActivityTileSkeleton = () => (
  <Paper
    variant="outlined"
    sx={{
      borderRadius: 2,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
    }}
  >
    <Skeleton
      variant="rectangular"
      animation="wave"
      sx={{ height: { xs: 80, sm: 100, md: 110 }, width: '100%' }}
    />
    <Box sx={{ p: { xs: 0.75, sm: 1 } }}>
      <Skeleton animation="wave" width="75%" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }} />
      <Skeleton animation="wave" width="55%" sx={{ fontSize: { xs: '0.6rem', sm: '0.65rem' } }} />
      <Skeleton animation="wave" width="40%" sx={{ fontSize: { xs: '0.6rem', sm: '0.65rem' } }} />
    </Box>
  </Paper>
);

const ActivityTilesSkeleton = () => (
  <Box sx={{ overflow: 'hidden', width: '100%' }}>
    <Box sx={{ display: 'flex', gap: 1.2 }}>
      {Array.from({ length: ITEMS_PER_VIEW }).map((_, index) => (
        <Box key={index} sx={tileFlexSx}>
          <ActivityTileSkeleton />
        </Box>
      ))}
    </Box>
  </Box>
);

const WorkstationCardSkeleton = () => (
  <Card
    variant="outlined"
    sx={{
      borderRadius: 3,
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      borderTop: '3px solid #e2e8f0',
      boxShadow: '0 10px 30px rgba(15, 23, 42, 0.08)',
    }}
  >
    <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, flex: 1, p: { xs: 1.5, sm: 2 } }}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
        <Skeleton
          variant="circular"
          animation="wave"
          sx={{ width: { xs: 36, sm: 42 }, height: { xs: 36, sm: 42 }, flexShrink: 0 }}
        />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Skeleton animation="wave" width="55%" sx={{ fontSize: '1rem' }} />
          <Skeleton animation="wave" width="40%" sx={{ fontSize: '0.75rem' }} />
        </Box>
        <Skeleton variant="rounded" animation="wave" width={72} height={24} sx={{ borderRadius: 4 }} />
      </Stack>

      <Skeleton variant="rounded" animation="wave" width={130} height={24} sx={{ borderRadius: 4 }} />

      <Divider />

      <Box>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1, minHeight: 26 }}>
          <Skeleton animation="wave" width={100} sx={{ fontSize: '0.75rem' }} />
          <Skeleton animation="wave" width={70} sx={{ fontSize: '0.75rem' }} />
        </Stack>
        <ActivityTilesSkeleton />
      </Box>
    </CardContent>
  </Card>
);

const WorkstationCard = ({
  workstation,
  snapshots,
  snapshotsLoading,
  onOpenSnapshot,
  onOpenTimeline,
  emptyMessage,
}) => {
  const [carouselIndex, setCarouselIndex] = useState(0);
  const total = snapshots.length;
  const hasMultiplePages = total > ITEMS_PER_VIEW;
  const maxIndex = Math.max(0, total - ITEMS_PER_VIEW);
  const occupied = workstation.status === 'occupied';

  useEffect(() => {
    setCarouselIndex((prev) => Math.min(prev, Math.max(0, total - ITEMS_PER_VIEW)));
  }, [total]);

  const stopCardClick = (event) => {
    event?.stopPropagation?.();
  };

  return (
    <Card
      variant="outlined"
      onClick={() => onOpenTimeline?.(workstation)}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpenTimeline?.(workstation);
        }
      }}
      sx={{
        borderRadius: 3,
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderTop: `3px solid ${occupied ? '#16a34a' : '#94a3b8'}`,
        boxShadow: '0 10px 30px rgba(15, 23, 42, 0.08)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        cursor: 'pointer',
        '&:hover': {
          boxShadow: '0 20px 40px rgba(15,23,42,0.15)',
          transform: 'translateY(-4px)',
        },
      }}
    >
      <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, flex: 1, p: { xs: 1.5, sm: 2 } }}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
          <Avatar
            sx={{
              width: { xs: 36, sm: 42 },
              height: { xs: 36, sm: 42 },
              bgcolor: '#EEF2FF',
              color: 'primary.main',
              flexShrink: 0,
            }}
          >
            <DesktopWindowsIcon fontSize="small" />
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="subtitle1" noWrap sx={{ fontWeight: 700 }}>
              {workstation.label}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              Last activity: {formatRelativeActivity(workstation.lastActivityAt)}
            </Typography>
          </Box>
          <Chip
            size="small"
            label={occupied ? 'Occupied' : 'Idle'}
            sx={{
              fontWeight: 600,
              bgcolor: occupied ? '#dcfce7' : '#f1f5f9',
              color: occupied ? '#166534' : '#475569',
            }}
          />
        </Stack>

        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          <Chip
            size="small"
            variant="outlined"
            color={occupied ? 'success' : 'default'}
            label={`Duration: ${formatDuration(workstation.totalDurationSec)}`}
            sx={{ height: 24, fontSize: '0.7rem' }}
          />
        </Stack>

        <Divider />

        <Box>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
            >
              Recent activity
            </Typography>
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              onClick={stopCardClick}
              sx={{ visibility: hasMultiplePages ? 'visible' : 'hidden' }}
            >
              <IconButton
                size="small"
                onClick={(e) => {
                  stopCardClick(e);
                  setCarouselIndex((p) => Math.max(0, p - ITEMS_PER_VIEW));
                }}
                disabled={carouselIndex === 0}
                sx={{ width: 26, height: 26 }}
              >
                <ChevronLeftIcon fontSize="small" />
              </IconButton>
              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 34, textAlign: 'center' }}>
                {Math.floor(carouselIndex / ITEMS_PER_VIEW) + 1}/{Math.max(1, Math.ceil(total / ITEMS_PER_VIEW))}
              </Typography>
              <IconButton
                size="small"
                onClick={(e) => {
                  stopCardClick(e);
                  setCarouselIndex((p) => Math.min(maxIndex, p + ITEMS_PER_VIEW));
                }}
                disabled={carouselIndex >= maxIndex}
                sx={{ width: 26, height: 26 }}
              >
                <ChevronRightIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Stack>

          {snapshotsLoading && total === 0 ? (
            <ActivityTilesSkeleton />
          ) : total === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              {emptyMessage || 'No snapshots yet today. A frame is saved when this desk becomes occupied, and again when it goes idle.'}
            </Typography>
          ) : (
            <Box sx={{ overflow: 'hidden', width: '100%' }} onClick={stopCardClick}>
              <Box
                sx={{
                  display: 'flex',
                  gap: 1.2,
                  transform: `translateX(-${carouselIndex * (100 / ITEMS_PER_VIEW)}%)`,
                  transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              >
                {snapshots.map((snapshot, index) => (
                  <Box key={snapshot.id} sx={tileFlexSx}>
                    <ActivityTile
                      snapshot={snapshot}
                      label={workstation.label}
                      onOpen={() => onOpenSnapshot(workstation, index)}
                    />
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

const WorkspacesView = () => {
  const { cameraId } = useParams();
  const { openImagePreview } = useImagePreview();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cameras, setCameras] = useState([]);
  const [laneRois, setLaneRois] = useState({});
  const [summaryItems, setSummaryItems] = useState([]);
  const [snapshotsByWorkstation, setSnapshotsByWorkstation] = useState({});
  const [snapshotsLoading, setSnapshotsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [pages,setPages] = useState(1);
const [pageSize,setPageSize] = useState(10);
  const [selectedWorkstationId, setSelectedWorkstationId] = useState('all');
  const [dateRange, setDateRange] = useState(() => createTodayDateRange());
  const [startTime, setStartTime] = useState(DEFAULT_START_TIME);
  const [endTime, setEndTime] = useState(DEFAULT_END_TIME);
  const [datePickerAnchor, setDatePickerAnchor] = useState(null);
  const [appliedQuery, setAppliedQuery] = useState(() =>
    createAppliedQuery(createTodayDateRange(), DEFAULT_START_TIME, DEFAULT_END_TIME),
  );
  const datePickerOpen = Boolean(datePickerAnchor);
  const [timelineModal, setTimelineModal] = useState({
    open: false,
    workstation: null,
    timeline: null,
    loading: false,
  });

  const mountedRef = useRef(true);
  const snapshotUrlCacheRef = useRef({});
  const snapshotRequestRef = useRef(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const camera = useMemo(
    () => cameras.find((c) => String(c.camera_id ?? c.id) === String(cameraId)) || null,
    [cameras, cameraId],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getWorkstationCameras();
        if (cancelled) return;
        setCameras(Array.isArray(res?.cameras) ? res.cameras : []);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load workspaces');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadWorkstationData = useCallback(async ({ silent = false } = {}) => {
    if (!cameraId) return;
    if (!silent) {
      setLoading(true);
      setError('');
    }
    try {
      const [roiRes, summaryRes] = await Promise.all([
        getROIConfig(WORKSTATION_TYPE, { cameraId }).catch(() => null),
        getWorkstationOccupancySummary({
          cameraId,
          startDate: appliedQuery.startDate,
          endDate: appliedQuery.endDate,
          startTime: appliedQuery.startTime,
          endTime: appliedQuery.endTime,
        }).catch(() => null),
      ]);
      if (!mountedRef.current) return;
      setLaneRois(roiRes?.lane_rois && typeof roiRes.lane_rois === 'object' ? roiRes.lane_rois : {});
      setSummaryItems(Array.isArray(summaryRes?.items) ? summaryRes.items : []);
    } catch (err) {
      if (mountedRef.current && !silent) setError(err.message || 'Failed to load workstation data');
    } finally {
      if (mountedRef.current && !silent) setLoading(false);
    }
  }, [appliedQuery, cameraId]);

  const loadSnapshots = useCallback(async () => {
    if (!cameraId) return;
    const requestId = ++snapshotRequestRef.current;
    try {
      const response = await getWorkstationSnapshots(cameraId, {
        startDate: appliedQuery.startDate,
        endDate: appliedQuery.endDate,
        startTime: appliedQuery.startTime,
        endTime: appliedQuery.endTime,
      });
      const rows = Array.isArray(response?.snapshots) ? response.snapshots : [];
      const loaded = await Promise.all(rows.map(async (row) => {
        const snapshotId = String(row?.snapshot_id || '').trim();
        const workstationId = String(row?.workstation_id || '').trim();
        if (!snapshotId || !workstationId) return null;
        let src = snapshotUrlCacheRef.current[snapshotId];
        if (src === undefined) {
          if (!row.image_available) {
            src = null;
          } else {
            try {
              const blob = await getWorkstationSnapshotImageBlob(snapshotId);
              src = await blobToDataUrl(blob);
            } catch (_) {
              src = null;
            }
          }
          snapshotUrlCacheRef.current[snapshotId] = src;
        }
        return {
          id: snapshotId,
          workstationId,
          src: src || null,
          capturedAt: row.captured_at,
          state: String(row.state || '').toLowerCase(),
        };
      }));
      if (!mountedRef.current || snapshotRequestRef.current !== requestId) return;
      const grouped = {};
      loaded.forEach((entry) => {
        if (!entry) return;
        if (!grouped[entry.workstationId]) grouped[entry.workstationId] = [];
        grouped[entry.workstationId].push(entry);
      });
      setSnapshotsByWorkstation(grouped);
    } catch (err) {
      if (mountedRef.current && snapshotRequestRef.current === requestId) {
        setError(err.message || 'Failed to load workstation snapshots');
      }
    } finally {
      if (mountedRef.current && snapshotRequestRef.current === requestId) {
        setSnapshotsLoading(false);
      }
    }
  }, [appliedQuery, cameraId]);

  useEffect(() => {
    setSnapshotsByWorkstation({});
    setSnapshotsLoading(true);
    void loadWorkstationData();
    void loadSnapshots();
  }, [loadWorkstationData, loadSnapshots]);

  useEffect(() => {
    setSelectedWorkstationId('all');
    setPages(1);
    setSnapshotsByWorkstation({});
    setTimelineModal({
      open: false,
      workstation: null,
      timeline: null,
      loading: false,
    });
  }, [cameraId]);

  const workstations = useMemo(() => {
    const summaryById = new Map(
      summaryItems.map((item) => [String(item.workstation_id ?? '').trim(), item]),
    );
    // Only desks that actually have an ROI drawn on THIS workspace camera.
    // Summary is used purely for enrichment; it never adds workstations.
    const roiIds = Object.keys(laneRois || {}).filter(Boolean);
    const ids = roiIds.length ? roiIds : [...summaryById.keys()].filter(Boolean);
    return sortWorkstationIds(ids).map((id) => {
      const item = summaryById.get(id) || {};
      return {
        id,
        label: formatWorkstationLabel(id),
        rect: laneRois?.[id] || null,
        status: String(item.status || 'idle').toLowerCase(),
        totalDurationSec: item.total_duration_sec ?? 0,
        lastActivityAt: item.last_activity_at || null,
      };
    });
  }, [laneRois, summaryItems]);

  const visibleWorkstations = useMemo(() => {
    const q = search.trim().toLowerCase();
    return workstations.filter((w) => {
      if (selectedWorkstationId !== 'all' && w.id !== selectedWorkstationId) return false;
      if (!q) return true;
      return w.label.toLowerCase().includes(q) || w.id.toLowerCase().includes(q);
    });
  }, [workstations, search, selectedWorkstationId]);

  const totalPages = Math.ceil(visibleWorkstations.length / pageSize);

const paginatedWorkstations = useMemo(() => {
  const startIndex = (pages - 1) * pageSize;
  return visibleWorkstations.slice(startIndex, startIndex + pageSize);
}, [visibleWorkstations, pages, pageSize]);

const startItem =
  visibleWorkstations.length === 0 ? 0 : (pages - 1) * pageSize + 1;

const endItem = Math.min(
  pages * pageSize,
  visibleWorkstations.length
);

// Reset to page 1 if filtering reduces the number of pages.
useEffect(() => {
  if (pages > Math.max(1, totalPages)) {
    setPages(Math.max(1, totalPages));
  }
}, [pages, totalPages]);

  useEffect(() => {
    const timer = setInterval(() => {
      void loadWorkstationData({ silent: true });
      void loadSnapshots();
    }, OCCUPANCY_REFRESH_MS);
    return () => clearInterval(timer);
  }, [loadSnapshots, loadWorkstationData]);

  const handleOpenSnapshot = useCallback(
    (workstation, index) => {
      const list = snapshotsByWorkstation[workstation.id] || [];
      openImagePreview({
        src: list[index]?.src,
        title: `${workstation.label} — ${camera?.label || 'Workspace'}`,
        alt: workstation.label,
        images: list.map((snap) => ({
          src: snap.src,
          title: `${workstation.label} — ${
            snap.state === 'occupied' ? 'Occupied' : snap.state === 'idle' ? 'Idle' : 'Snapshot'
          } ${formatDate(snap.capturedAt, true, true)}`,
          alt: workstation.label,
        })),
        index,
        hideTitle: false,
      });
    },
    [snapshotsByWorkstation, openImagePreview, camera],
  );

  const handleOpenTimeline = useCallback(
    async (workstation) => {
      if (!workstation?.id || !cameraId) return;
      setTimelineModal({
        open: true,
        workstation,
        timeline: null,
        loading: true,
      });
      try {
        const data = await getWorkstationOccupancyTimeline({
          cameraId,
          workstationId: workstation.id,
          startDate: appliedQuery.startDate,
          endDate: appliedQuery.endDate,
          startTime: appliedQuery.startTime,
          endTime: appliedQuery.endTime,
        });
        if (!mountedRef.current) return;
        setTimelineModal((prev) => ({
          ...prev,
          timeline: data,
          loading: false,
        }));
      } catch (err) {
        if (!mountedRef.current) return;
        setError(err.message || 'Failed to load workstation timeline');
        setTimelineModal((prev) => ({
          ...prev,
          timeline: {
            summary_date: appliedQuery.startDate,
            end_date: appliedQuery.endDate,
            total_duration_sec: workstation.totalDurationSec || 0,
            status: workstation.status,
            events: [],
          },
          loading: false,
        }));
      }
    },
    [appliedQuery, cameraId],
  );

  const handleCloseTimeline = useCallback(() => {
    setTimelineModal({
      open: false,
      workstation: null,
      timeline: null,
      loading: false,
    });
  }, []);

  // If the date filter changes while the modal is open, refresh timeline for that day.
  useEffect(() => {
    if (!timelineModal.open || !timelineModal.workstation?.id) return;
    let cancelled = false;
    (async () => {
      setTimelineModal((prev) => ({ ...prev, loading: true }));
      try {
        const data = await getWorkstationOccupancyTimeline({
          cameraId,
          workstationId: timelineModal.workstation.id,
          startDate: appliedQuery.startDate,
          endDate: appliedQuery.endDate,
          startTime: appliedQuery.startTime,
          endTime: appliedQuery.endTime,
        });
        if (cancelled || !mountedRef.current) return;
        setTimelineModal((prev) => ({
          ...prev,
          timeline: data,
          loading: false,
        }));
      } catch (_) {
        if (cancelled || !mountedRef.current) return;
        setTimelineModal((prev) => ({ ...prev, loading: false }));
      }
    })();
    return () => {
      cancelled = true;
    };
    // Only re-fetch when the applied range changes while open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedQuery]);

  const occupiedCount = workstations.filter((w) => w.status === 'occupied').length;
  const visibleRange = datePickerOpen
    ? {
        startDate: toDateKey(dateRange?.[0]?.startDate),
        endDate: toDateKey(dateRange?.[0]?.endDate || dateRange?.[0]?.startDate),
      }
    : appliedQuery;
  const appliedRangeLabel = formatRangeLabel(visibleRange.startDate, visibleRange.endDate);
  const snapshotEmptyMessage = useMemo(() => {
    const isFullToday =
      appliedQuery.startDate === todayLocalDate() &&
      appliedQuery.endDate === todayLocalDate() &&
      appliedQuery.startTime === DEFAULT_START_TIME &&
      appliedQuery.endTime === DEFAULT_END_TIME;
    if (isFullToday) {
      return 'No snapshots yet today. A frame is saved when this desk becomes occupied, and again when it goes idle.';
    }
    return 'No snapshots in this date range. A frame is saved when this desk becomes occupied, and again when it goes idle.';
  }, [appliedQuery]);
  const liveDate = useMemo(() => {
    const start = parseDateKey(appliedQuery.startDate);
    const [startHour, startMinute] = String(appliedQuery.startTime || DEFAULT_START_TIME).split(':').map(Number);
    start.setHours(Number.isFinite(startHour) ? startHour : 0, Number.isFinite(startMinute) ? startMinute : 0, 0, 0);
    const end = parseDateKey(appliedQuery.endDate);
    const [endHour, endMinute] = String(appliedQuery.endTime || DEFAULT_END_TIME).split(':').map(Number);
    end.setHours(Number.isFinite(endHour) ? endHour : 23, Number.isFinite(endMinute) ? endMinute : 59, 59, 0);
    const now = new Date();
    if (now < start || now > end) return null;
    return todayLocalDate();
  }, [appliedQuery]);

  const openDatePicker = (event) => {
    setDateRange([{
      startDate: parseDateKey(appliedQuery.startDate),
      endDate: parseDateKey(appliedQuery.endDate),
      key: 'selection',
    }]);
    setStartTime(appliedQuery.startTime || DEFAULT_START_TIME);
    setEndTime(appliedQuery.endTime || DEFAULT_END_TIME);
    setDatePickerAnchor(event.currentTarget);
  };

  const applyDateFilter = () => {
    setAppliedQuery(createAppliedQuery(dateRange, startTime, endTime));
    setDatePickerAnchor(null);
  };

  return (
    <Container maxWidth="2200" disableGutters sx={{ px: 0 }}>
      <PageHeader
        title="Workspaces"
        subtitle={
          camera
            ? `Live workstation activity for ${camera.label || camera.camera_id || cameraId}`
            : 'Live workstation activity per workspace camera'
        }
        icon={DesktopWindowsIcon}
        titleAdornment={
          workstations.length > 0 ? (
            <Chip
              size="small"
              color="primary"
              variant="outlined"
              label={`${occupiedCount}/${workstations.length} Occupied`}
              sx={{ fontWeight: 700, mt: { xs: 0.25, sm: 0.5 } }}
            />
          ) : null
        }
      />

      <Box sx={pageFiltersToolbarSx}>
        <Stack {...pageFiltersStackSx}>
          <Box sx={pageFiltersControlsSx}>
            <TextField
              size="small"
              placeholder="Search workstation"
              value={search}
onChange={(e) => {
  setSearch(e.target.value);
  setPages(1);
}}              sx={pageFilterAutocompleteSx(220)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl size="small" sx={pageFilterFormControlSx(220)}>
              <InputLabel id="workstation-select-label">Workstation</InputLabel>
              <Select
                labelId="workstation-select-label"
                label="Workstation"
                value={selectedWorkstationId}
onChange={(e) => {
  setSelectedWorkstationId(e.target.value);
  setPages(1);
}}              >
                <MenuItem value="all">All workstations</MenuItem>
                {workstations.map((w) => (
                  <MenuItem key={w.id} value={w.id}>
                    {w.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box onClick={openDatePicker} sx={pageFilterDatePickerSx(220)}>
              <CalendarMonthIcon fontSize="small" color="action" />
              <Typography variant="body2" noWrap sx={{ flex: 1, fontSize: '0.875rem' }}>
                {appliedRangeLabel}
              </Typography>
            </Box>
            <Popover
              open={datePickerOpen}
              anchorEl={datePickerAnchor}
              onClose={() => setDatePickerAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              slotProps={{
                paper: {
                  sx: { maxWidth: '100vw', overflow: 'auto' },
                },
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
                <Box
                  sx={{
                    p: 2,
                    borderRight: { md: '1px solid' },
                    borderBottom: { xs: '1px solid', md: 'none' },
                    borderColor: 'divider',
                    minWidth: 140,
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mb: 1, display: 'block', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}
                  >
                    Quick Select
                  </Typography>
                  <Stack spacing={0.5}>
                    {[
                      { label: 'Today', getValue: () => ({ startDate: new Date(), endDate: new Date() }) },
                      { label: 'Yesterday', getValue: () => ({ startDate: addDays(new Date(), -1), endDate: addDays(new Date(), -1) }) },
                      { label: 'Last 7 Days', getValue: () => ({ startDate: addDays(new Date(), -6), endDate: new Date() }) },
                      { label: 'Last 30 Days', getValue: () => ({ startDate: addDays(new Date(), -29), endDate: new Date() }) },
                      {
                        label: 'This Month',
                        getValue: () => {
                          const now = new Date();
                          return { startDate: new Date(now.getFullYear(), now.getMonth(), 1), endDate: now };
                        },
                      },
                      {
                        label: 'Last Month',
                        getValue: () => {
                          const now = new Date();
                          return {
                            startDate: new Date(now.getFullYear(), now.getMonth() - 1, 1),
                            endDate: new Date(now.getFullYear(), now.getMonth(), 0),
                          };
                        },
                      },
                    ].map((preset) => (
                      <Button
                        key={preset.label}
                        size="small"
                        variant="text"
                        onClick={() => {
                          const { startDate, endDate } = preset.getValue();
                          setDateRange([{ startDate, endDate, key: 'selection' }]);
                        }}
                        sx={{
                          justifyContent: 'flex-start',
                          textTransform: 'none',
                          px: 1.5,
                          py: 0.75,
                          color: 'text.primary',
                          '&:hover': { bgcolor: 'action.hover' },
                        }}
                      >
                        {preset.label}
                      </Button>
                    ))}
                  </Stack>
                </Box>
                <Box
                  sx={{
                    '& .rdrCalendarWrapper': { fontSize: { xs: '0.8rem', sm: '0.875rem' } },
                    '& .rdrMonth': { width: { xs: '280px', sm: '300px' } },
                  }}
                >
                  <DateRangePicker
                    ranges={dateRange}
                    onChange={(ranges) => setDateRange([ranges.selection])}
                    moveRangeOnFirstSelection={false}
                    months={isMobile ? 1 : 2}
                    direction={isMobile ? 'vertical' : 'horizontal'}
                    rangeColors={[primary]}
                    staticRanges={[]}
                    inputRanges={[]}
                    maxDate={new Date()}
                  />
                  <Box sx={{ px: 2, pb: 2, pt: 1, display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
                    <TextField
                      label="Start Time"
                      type="time"
                      size="small"
                      value={startTime}
                      onChange={(e) => setStartTime(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ step: 60 }}
                      fullWidth
                    />
                    <TextField
                      label="End Time"
                      type="time"
                      size="small"
                      value={endTime}
                      onChange={(e) => setEndTime(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ step: 60 }}
                      fullWidth
                    />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', alignItems: 'center', p: 2, pt: 0 }}>
                    <Button
                      onClick={() => {
                        setDateRange(createTodayDateRange());
                        setStartTime(DEFAULT_START_TIME);
                        setEndTime(DEFAULT_END_TIME);
                      }}
                      variant="outlined"
                      size="small"
                      startIcon={<RefreshIcon />}
                    >
                      Reset
                    </Button>
                    <Button onClick={() => setDatePickerAnchor(null)} variant="outlined" size="small">
                      Cancel
                    </Button>
                    <Button onClick={applyDateFilter} variant="contained" size="small">
                      Apply
                    </Button>
                  </Box>
                </Box>
              </Box>
            </Popover>
          </Box>
        </Stack>
      </Box>

      {error && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box
          sx={{
            display: 'grid',
            gap: { xs: 2, sm: 2.5 },
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, minmax(0, 1fr))',
              lg: 'repeat(3, minmax(0, 1fr))',
            },
          }}
        >
          {Array.from({ length: workstations.length || ITEMS_PER_VIEW }).map((_, index) => (
            <WorkstationCardSkeleton key={index} />
          ))}
        </Box>
      ) : visibleWorkstations.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 2, background: '#fafafa' }}>
          <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
            No workstations found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {search || selectedWorkstationId !== 'all'
              ? 'No workstation matches the current filters.'
              : 'Mark workstations for this workspace from Workstation Monitoring → Manage → Mark Workstations.'}
          </Typography>
        </Paper>
      
) : (
  <>
    <Box
      sx={{
        display: 'grid',
        gap: { xs: 2, sm: 2.5 },
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, minmax(0, 1fr))',
          lg: 'repeat(3, minmax(0, 1fr))',
        },
      }}
    >
      {paginatedWorkstations.map((workstation) => (
        <WorkstationCard
          key={workstation.id}
          workstation={workstation}
          snapshots={snapshotsByWorkstation[workstation.id] || []}
          snapshotsLoading={snapshotsLoading}
          onOpenSnapshot={handleOpenSnapshot}
          onOpenTimeline={handleOpenTimeline}
          emptyMessage={snapshotEmptyMessage}
        />
      ))}
    </Box>

    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={2}
      alignItems="center"
      justifyContent="space-between"
      sx={{ mt: 3, mb: 2 }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        flexWrap="wrap"
      >
        <Typography variant="body2" color="text.secondary">
          Showing {startItem}-{endItem} of {visibleWorkstations.length}
        </Typography>

        <Typography variant="body2" color="text.secondary">
          Rows per page:
        </Typography>

        <FormControl size="small" sx={{ minWidth: 80 }}>
          <Select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPages(1);
            }}
            inputProps={{ 'aria-label': 'Rows per page' }}
          >
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={25}>25</MenuItem>
            <MenuItem value={50}>50</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      <Pagination
        count={Math.max(1, totalPages)}
        page={pages}
        onChange={(_, newPage) => setPages(newPage)}
        showFirstButton
        showLastButton
        color="primary"
        size="medium"
      />
    </Stack>
  </>

      )}

      <WorkstationTimelineModal
        open={timelineModal.open}
        onClose={handleCloseTimeline}
        workstationLabel={timelineModal.workstation?.label}
        summaryDate={appliedQuery.startDate}
        rangeLabel={formatRangeLabel(appliedQuery.startDate, appliedQuery.endDate)}
        liveDate={liveDate}
        timeline={timelineModal.timeline}
        loading={timelineModal.loading}
        thumbnail={(snapshotsByWorkstation[timelineModal.workstation?.id] || [])[0]?.src || null}
      />
    </Container>
  );
};

export default WorkspacesView;
